import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import {
  MagnifyingGlass, CurrencyInr, ArrowLeft, Package, CheckCircle, QrCode
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday } from "@/lib/istDate";
import { normalizePhone, cleanPhoneInput, formatPhone } from "@/lib/phone";

const inr2 = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function QuickPaymentDialog({ open, onClose, onSaved, prefillOrderId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  // Party drill-down: when the user clicks a customer group card, we show
  // that party's orders in a sub-list. null = grouped list, object = drill.
  const [partyDrill, setPartyDrill] = useState(null);
  const [modes, setModes] = useState([]);
  const [payAmt, setPayAmt] = useState("");
  const [payMode, setPayMode] = useState("cash");
  // Party-level payment state (opens when user clicks "PAY DUE" on the
  // grouped card — records one lump sum distributed FIFO by backend).
  const [payParty, setPayParty] = useState(null);
  const [partyAmt, setPartyAmt] = useState("");
  const [partyDiscountAmt, setPartyDiscountAmt] = useState("");
  const [partyNote, setPartyNote] = useState("");
  // QR toggle for party payment — reuses QrPanel and admin's selected payMode
  // so the QR reflects EXACTLY the bank account chosen in the MODE dropdown.
  const [showPartyQr, setShowPartyQr] = useState(false);
  // Back-dating: default to today (YYYY-MM-DD in local tz). Owner can change
  // this if payment was received on a previous day but recorded later.
  const [paidOn, setPaidOn] = useState(() => istToday());
  const [discountAmt, setDiscountAmt] = useState("");
  const [discType, setDiscType] = useState("flat");   // flat | pct | per_sqft (order total)
  const [discVal, setDiscVal] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [markDelivered, setMarkDelivered] = useState(false);
  const [showQr, setShowQr] = useState(false);
  // Standalone advance: null when hidden. When set — either from an
  // existing party (mobile locked) or a brand new customer (all editable)
  // — the dialog swaps its body to the take-advance form.
  const [advanceCtx, setAdvanceCtx] = useState(null); // { mobile, customer_name, address, isNew }
  const [advAmt, setAdvAmt] = useState("");
  const [advName, setAdvName] = useState("");
  const [advMobile, setAdvMobile] = useState("");
  const [advAddress, setAdvAddress] = useState("");
  const [advNote, setAdvNote] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/settings/payment-modes");
        setModes(data);
        if (data?.length) setPayMode(data[0].key);
      } catch (_e) { /* ignore */ }
    })();
  }, [open]);

  // Auto-select an order if a prefillOrderId is provided
  useEffect(() => {
    if (open && prefillOrderId) {
      (async () => {
        try {
          const { data } = await api.get(`/orders/${prefillOrderId}`);
          setSelected(data);
          setPayAmt(data.balance > 0 ? String(data.balance) : "");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      })();
    }
  }, [open, prefillOrderId]);

  // reset on close
  useEffect(() => {
    if (!open) {
      setQ(""); setResults([]); setSelected(null); setPartyDrill(null);
      setPayAmt(""); setNote(""); setMarkDelivered(false);
      setDiscountAmt(""); setDiscVal(""); setDiscType("flat");
      setPayParty(null); setPartyAmt(""); setPartyDiscountAmt(""); setPartyNote("");
      setShowPartyQr(false);
      setPaidOn(istToday());
      setAdvanceCtx(null); setAdvAmt(""); setAdvName(""); setAdvMobile(""); setAdvAddress(""); setAdvNote("");
    }
  }, [open]);

  // Open TAKE ADVANCE for a specific (existing) party OR for a brand-new
  // customer. Shared entry point so both flows land in the same form.
  const openAdvance = (ctx) => {
    setAdvanceCtx(ctx);
    setAdvName(ctx?.customer_name || "");
    setAdvMobile(ctx?.mobile || "");
    setAdvAddress(ctx?.address || "");
    setAdvAmt("");
    setAdvNote("");
  };

  const submitAdvance = async () => {
    const amt = Number(advAmt);
    if (!amt || amt <= 0) { toast.error("Amount must be > 0"); return; }
    const mob = advMobile.trim();
    if (!mob) { toast.error("Mobile required"); return; }
    if (!advanceCtx?.isNew && advanceCtx?.mobile && mob !== advanceCtx.mobile) {
      // Owner shouldn't accidentally clobber an existing party's mobile
      toast.error("Existing party ka mobile edit nahi kar sakte");
      return;
    }
    if (advanceCtx?.isNew && !advName.trim()) {
      toast.error("Customer name required for new party");
      return;
    }
    setBusy(true);
    try {
      await api.post("/parties/advance", {
        mobile: mob,
        customer_name: advName.trim(),
        address: advAddress.trim(),
        amount: amt,
        mode: payMode,
        note: advNote.trim(),
        paid_on: paidOn,
      });
      toast.success(`₹${inr2(amt)} advance ${advanceCtx?.isNew ? "recorded for new party" : "added to party credit"}`);
      onSaved?.();
      // Reset — return to search view
      setAdvanceCtx(null);
      setAdvAmt(""); setAdvName(""); setAdvMobile(""); setAdvAddress(""); setAdvNote("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Party-level authoritative balances (source of truth = /parties, same
  // aggregation as the Party page). We look these up by normalized mobile
  // and OVERRIDE the client-side sum-of-orders total_due so the two views
  // (Party page vs Payment-In dialog) always agree.
  // Ref: 2026-09-21 Munna Sah ₹49,933 vs ₹30,832 mismatch (25 orders vs
  // /orders/search 20-order truncation + potentially stale order.balance).
  const [partyBalances, setPartyBalances] = useState({});   // { phone_normalized: get_customer_balance() }
  const last10 = (m) => (String(m || "").replace(/\D/g, "").slice(-10));
  // Same stale-response guard as GlobalSearch (see 2026-09-22 fix).
  const genRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      if (abortRef.current) { try { abortRef.current.abort(); } catch (_e) {} }
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const myGen = ++genRef.current;
      try {
        const [ordRes, partyRes] = await Promise.all([
          api.get(`/orders/search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal }),
          api.get(`/parties/due-search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal }).catch(() => ({ data: { parties: [] } })),
        ]);
        if (myGen !== genRef.current) return;   // stale — ignore
        const openStates = ["new_order", "design_pending", "ready_to_cut", "in_cutting", "completed"];
        const data = ordRes.data || [];
        data.sort((a, b) => {
          const ao = openStates.includes(a.status) ? 0 : 1;
          const bo = openStates.includes(b.status) ? 0 : 1;
          return ao - bo;
        });
        setResults(data);
        const map = {};
        for (const p of (partyRes.data?.parties || [])) map[p.mobile] = p;
        setPartyBalances(map);
      } catch (e) {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // ONE card per customer (normalized phone). Dues + pending orders come from
  // the backend single-source-of-truth (get_customer_balance, FIFO allocated).
  const parties = React.useMemo(() => {
    const byKey = new Map();
    for (const b of Object.values(partyBalances)) {
      const fifo = {};
      for (const o of b.orders || []) fifo[o.order_id] = o.remaining;
      byKey.set(b.mobile, {
        key: b.mobile, customer_name: b.customer_name || "—", mobile: b.mobile,
        total_due: Number(b.balance_due) || 0, advance_credit: Number(b.advance_credit) || 0,
        pending_count: b.pending_count, authoritative_order_count: b.order_count,
        fifo, orders: [],
      });
    }
    for (const r of results || []) {
      const m10 = normalizePhone(r.mobile) || last10(r.mobile);
      const key = (m10 && byKey.has(m10)) ? m10 : (m10 || `name:${(r.customer_name || "").trim().toLowerCase()}`);
      if (!byKey.has(key)) {
        byKey.set(key, { key, customer_name: r.customer_name || "—", mobile: r.mobile || "", total_due: 0,
                         advance_credit: 0, pending_count: 0, fifo: null, orders: [] });
      }
      const g = byKey.get(key);
      g.orders.push(r);
      if (!g.fifo && Number(r.balance) > 0) { g.total_due += Number(r.balance); g.pending_count += 1; }
    }
    for (const g of byKey.values()) {
      if (g.fifo) g.orders.sort((a, b) => (g.fifo[b.id] > 0) - (g.fifo[a.id] > 0));
    }
    return Array.from(byKey.values()).filter((g) => g.orders.length || g.total_due || g.advance_credit)
      .sort((a, b) => b.total_due - a.total_due);
  }, [results, partyBalances]);

  const pendingOf = (party, r) => (party?.fifo && r.id in party.fifo ? party.fifo[r.id] : Number(r.balance) || 0);

  const pickOrder = async (id, party = null) => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      const pend = party?.fifo && id in party.fifo ? party.fifo[id] : data.balance;
      setSelected({ ...data, fifo_pending: Math.round(pend * 100) / 100 });
      setPayAmt(pend > 0 ? String(Math.round(pend * 100) / 100) : "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Party payment — distributes a lump sum FIFO across all pending orders.
  // Optionally, a discount amount is also FIFO-applied so operator can close
  // out fractional dues in one shot (e.g. "customer paid ₹1000, waive off
  // ₹97.64 rest so the ledger settles").
  const submitPartyPayment = async (e) => {
    e.preventDefault();
    if (!payParty) return;
    const n = Number(partyAmt) || 0;
    const d = Number(partyDiscountAmt) || 0;
    if (n <= 0 && d <= 0) {
      toast.error("Amount ya Discount me se kuch to daaliye");
      return;
    }
    const totalCover = n + d;
    if (totalCover > payParty.total_due + 0.5) {
      const surplus = (totalCover - payParty.total_due).toFixed(2);
      const ok = window.confirm(
        `Total ₹${totalCover.toFixed(2)} ka payment / discount hai.\n` +
        `Due sirf ₹${payParty.total_due.toFixed(2)} hai — surplus ₹${surplus} party ke ADVANCE (credit) mein add ho jaayega, next order mein apne aap use ho jaayega.\n\nContinue?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      let paymentResult = null;
      let discountResult = null;
      if (n > 0) {
        const { data } = await api.post("/parties/payment", {
          mobile: payParty.mobile || null,
          customer_name: payParty.mobile ? null : payParty.customer_name,
          amount: n,
          mode: payMode,
          note: partyNote || "",
          paid_on: paidOn,
        });
        paymentResult = data;
      }
      if (d > 0) {
        const { data } = await api.post("/parties/discount", {
          mobile: payParty.mobile || null,
          customer_name: payParty.mobile ? null : payParty.customer_name,
          amount: d,
          note: partyNote || "Discount / waived off",
          // Link the discount to the payment split_tag so a subsequent
          // delete on the payment cascades and removes this write-off too.
          linked_payment_group: paymentResult?.split_tag || null,
        });
        discountResult = data;
      }
      const parts = [];
      if (paymentResult) parts.push(`₹${inr2(paymentResult.total_paid)} received`);
      if (discountResult) parts.push(`₹${inr2(discountResult.total_discount)} discount`);
      toast.success(parts.join(" + ") + " · ledger updated ✓");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      // Self-heal path: backend told us this party's ledger is already
      // cleared (stored balance was stale). Notify friendly + refresh.
      if (typeof detail === "string" && detail.startsWith("Party ka ledger already cleared")) {
        toast.info(detail);
        onSaved && onSaved();
        onClose();
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const n = Number(payAmt) || 0;
    const d = Number(discountAmt) || 0;
    if (n <= 0 && d <= 0) {
      toast.error("Enter a payment amount or a discount");
      return;
    }
    const bal = selected.fifo_pending ?? selected.balance ?? 0;
    if (d > bal) { toast.error(`Discount cannot exceed pending balance (₹${bal})`); return; }
    const extra = Math.round((n + d - bal) * 100) / 100;
    if (extra > 0.009) {
      const ok = window.confirm(
        `Is order ka pending sirf ₹${inr2(bal)} hai.\n` +
        `₹${inr2(extra)} extra will be adjusted to next pending orders / kept as advance.\n\nContinue?`);
      if (!ok) return;
    }
    const onOrder = Math.max(Math.min(n, Math.round((bal - d) * 100) / 100, Number(selected.balance) || 0), 0);
    const spill = Math.round((n - onOrder) * 100) / 100;
    setBusy(true);
    try {
      let updated = null;
      if (onOrder > 0) {
        const res = await api.post(
          `/orders/${selected.id}/payment`,
          { amount: onOrder, mode: payMode, note: note || "", paid_on: paidOn }
        );
        updated = res.data;
      }
      if (spill > 0) {
        await api.post("/parties/payment", {
          mobile: selected.mobile || null,
          customer_name: selected.mobile ? null : selected.customer_name,
          amount: spill, mode: payMode, paid_on: paidOn,
          note: `${note ? note + " · " : ""}Extra from #${selected.order_number}`,
        });
      }
      if (d > 0) {
        const res = await api.post(
          `/orders/${selected.id}/discount`,
          { amount: d, note: note || "Discount / waived off" }
        );
        updated = res.data;
      }
      // Optionally mark delivered
      if (markDelivered && updated && updated.balance <= 0 && updated.status === "completed") {
        await api.post(`/orders/${selected.id}/status`, { status: "delivered" });
      }
      const msgParts = [];
      if (n > 0) msgParts.push(`₹${n} received`);
      if (d > 0) msgParts.push(`₹${d} discount`);
      toast.success(`${msgParts.join(" + ")} on #${updated?.order_number || selected.order_number} ✓`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const balance = selected?.fifo_pending ?? selected?.balance ?? 0;
  const applyDisc = (type, val) => {
    setDiscType(type); setDiscVal(val);
    const v = Number(val) || 0;
    const amt = type === "pct" ? (selected?.total_amount || 0) * v / 100 : type === "per_sqft" ? (selected?.total_sqft || 0) * v : v;
    const da = Math.round(Math.max(Math.min(amt, balance), 0) * 100) / 100;
    setDiscountAmt(v > 0 ? String(da) : "");
    if (da > 0 && (Number(payAmt) || 0) + da > balance) setPayAmt(String(Math.max(Math.round((balance - da) * 100) / 100, 0)));
  };
  const canAutoDeliver = selected?.status === "completed" && balance > 0 && Number(payAmt) >= balance;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-lg bg-slate-900 border-slate-700 text-white rounded-sm overflow-x-hidden [&>*]:min-w-0"
        data-testid="quick-payment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 font-heading">
            <span className="flex items-center gap-2">
              <CurrencyInr size={20} className="text-orange-400" /> Payment In
            </span>
            {(selected || payParty) && (
              <span className="flex items-center gap-2">
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
                  DATE
                </Label>
                <input
                  type="date"
                  value={paidOn}
                  onChange={(e) => setPaidOn(e.target.value)}
                  max={istToday()}
                  className="bg-slate-800/60 border border-slate-600 hover:border-orange-500/60 focus:border-orange-500 focus:outline-none text-white h-9 rounded-sm font-mono-num px-2 text-sm"
                  data-testid="quick-payment-date"
                  title="Payment kis din receive hua? Purani date bhi de sakte hain."
                />
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {advanceCtx ? (
          /* Standalone advance — existing party OR brand-new customer */
          <form
            onSubmit={(e) => { e.preventDefault(); submitAdvance(); }}
            className="space-y-3"
            data-testid="take-advance-form"
          >
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAdvanceCtx(null)}
                className="text-slate-400 hover:text-cyan-300 text-xs font-mono-num flex items-center gap-1"
                data-testid="advance-back-btn"
              >
                <ArrowLeft size={14} /> BACK
              </button>
              <div className="text-xs font-mono-num tracking-widest text-cyan-400 ml-auto">
                {advanceCtx.isNew ? "NEW CUSTOMER ADVANCE" : "ADD ADVANCE"}
              </div>
            </div>

            <div className="p-3 rounded-sm bg-cyan-500/10 border border-cyan-500/40">
              <div className="text-[10px] font-mono-num tracking-widest text-cyan-300 mb-0.5">
                CUSTOMER DETAILS
              </div>
              <Input
                value={advName}
                onChange={(e) => setAdvName(e.target.value)}
                placeholder="Customer name *"
                disabled={!advanceCtx.isNew}
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mb-2 disabled:opacity-70"
                data-testid="advance-name-input"
              />
              <Input
                value={advMobile}
                onChange={(e) => setAdvMobile(e.target.value.replace(/[^0-9+\- ]/g, ""))}
                onBlur={() => setAdvMobile(cleanPhoneInput(advMobile))}
                placeholder="Mobile *"
                disabled={!advanceCtx.isNew}
                inputMode="tel"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mb-2 font-mono-num disabled:opacity-70"
                data-testid="advance-mobile-input"
              />
              <Input
                value={advAddress}
                onChange={(e) => setAdvAddress(e.target.value)}
                placeholder="Address (optional)"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
                data-testid="advance-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-mono-num tracking-widest text-slate-400">AMOUNT (₹) *</Label>
                <Input
                  type="number" min="1" step="0.01"
                  value={advAmt}
                  onChange={(e) => setAdvAmt(e.target.value)}
                  placeholder="10000"
                  autoFocus
                  className="mt-1.5 bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg"
                  data-testid="advance-amount-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono-num tracking-widest text-slate-400">PAID ON</Label>
                <Input
                  type="date"
                  value={paidOn}
                  max={istToday()}
                  onChange={(e) => setPaidOn(e.target.value)}
                  className="mt-1.5 bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num"
                  data-testid="advance-paidon-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-[10px] font-mono-num tracking-widest text-slate-400">PAYMENT MODE</Label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger className="mt-1.5 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" data-testid="advance-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-600 text-white">
                  {modes.map((m) => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] font-mono-num tracking-widest text-slate-400">NOTE (OPTIONAL)</Label>
              <Input
                value={advNote}
                onChange={(e) => setAdvNote(e.target.value)}
                placeholder="e.g., Gate order lene se pehle diya"
                className="mt-1.5 bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
                data-testid="advance-note-input"
              />
            </div>

            <div className="text-[11px] text-slate-400 bg-cyan-500/5 border border-cyan-500/30 rounded-sm p-2.5 font-mono-num">
              Ye advance party ke <span className="text-cyan-300 font-bold">CREDIT BALANCE</span> me save hoga.
              Jab bhi is customer ka naya order banega, is credit se automatic settle ho jayega.
            </div>

            <Button
              type="submit"
              disabled={busy || Number(advAmt) <= 0}
              className="w-full h-11 rounded-sm bg-cyan-500 hover:bg-cyan-600 text-white font-heading tracking-widest"
              data-testid="advance-submit-btn"
            >
              {busy ? "SAVING…" : (advanceCtx.isNew ? "RECORD ADVANCE" : `ADD ₹${inr2(Number(advAmt || 0))} TO CREDIT`)}
            </Button>
          </form>
        ) : payParty ? (
          /* Party-level payment — lump sum distributed FIFO by backend */
          <form onSubmit={submitPartyPayment} className="space-y-4" data-testid="party-payment-form">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setPayParty(null); setPartyAmt(""); setPartyDiscountAmt(""); setPartyNote(""); }}
                className="text-slate-400 hover:text-orange-300 text-xs font-mono-num flex items-center gap-1"
                data-testid="party-payment-back"
              >
                <ArrowLeft size={12} /> BACK
              </button>
            </div>
            <div className="p-4 rounded-sm bg-orange-500/10 border border-orange-500/40">
              <div className="text-[10px] font-mono-num tracking-widest text-orange-400">TOTAL DUE</div>
              <div className="font-heading text-3xl font-black text-orange-300 leading-none mt-0.5">
                ₹{inr2(payParty.total_due)}
              </div>
              <div className="text-sm text-slate-200 mt-1">
                {payParty.customer_name}
              </div>
              <div className="text-[10px] font-mono-num text-slate-400 mt-0.5">
                {payParty.mobile || "—"}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={partyAmt}
                  onChange={(e) => setPartyAmt(e.target.value)}
                  autoFocus
                  placeholder={String(payParty.total_due)}
                  className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg mt-1.5"
                  data-testid="party-payment-amount"
                />
              </div>
              <div>
                <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
                <Select value={payMode} onValueChange={setPayMode}>
                  <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1.5" data-testid="party-payment-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-600 text-white">
                    {modes.map((m) => (
                      <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (optional)</Label>
              <Input
                value={partyNote}
                onChange={(e) => setPartyNote(e.target.value)}
                placeholder="e.g., cheque #1234, UPI ref XYZ"
                className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                data-testid="party-payment-note"
              />
            </div>

            {/* Discount / write-off — distributed FIFO across the party's
                pending orders. Enables owner to zero-out fractional dues
                (e.g. paise) that customer refuses to pay. */}
            <div>
              <Label className="text-amber-300 font-mono-num text-[10px] tracking-widest flex items-center justify-between">
                <span>DISCOUNT / WRITE-OFF (₹)</span>
                <span className="text-slate-500 normal-case tracking-normal">
                  customer nahi de raha? yaha likh do
                </span>
              </Label>
              <Input
                type="number" min="0" step="0.01"
                value={partyDiscountAmt}
                onChange={(e) => setPartyDiscountAmt(e.target.value)}
                placeholder="0"
                className="bg-slate-800/60 border-amber-500/40 text-amber-200 placeholder:text-slate-600 h-11 rounded-sm font-mono-num text-lg mt-1.5 focus:border-amber-500"
                data-testid="party-payment-discount"
              />
              {(() => {
                const n = Number(partyAmt) || 0;
                const d = Number(partyDiscountAmt) || 0;
                const totalCover = n + d;
                const dueAfter = Math.max(payParty.total_due - totalCover, 0);
                const surplus = Math.max(totalCover - payParty.total_due, 0);
                if (n > 0 || d > 0) {
                  return (
                    <div className="text-[10px] font-mono-num text-slate-400 mt-1.5 flex items-center gap-2 flex-wrap">
                      <span>PAY ₹{inr2(n)}</span>
                      {d > 0 && <span className="text-amber-400">+ DISCOUNT ₹{inr2(d)}</span>}
                      <span className="text-slate-500">→</span>
                      {surplus > 0.01 ? (
                        <span className="text-cyan-300">LEDGER CLEARED ✓ · ₹{surplus.toFixed(2)} → ADVANCE</span>
                      ) : (
                        <span className={dueAfter <= 0.01 ? "text-emerald-300" : "text-orange-300"}>
                          {dueAfter <= 0.01 ? "LEDGER CLEARED ✓" : `₹${dueAfter.toFixed(2)} still due`}
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            {/* Generate QR — reuses admin's payment-mode QR endpoint so the
                QR ALWAYS matches the MODE dropdown selected above (UCO, SBI,
                Cash, whichever). Amount = partyAmt (or total_due if empty). */}
            <Button
              type="button"
              onClick={() => setShowPartyQr((v) => !v)}
              variant="outline"
              className="w-full rounded-sm border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 h-11 font-heading tracking-widest"
              data-testid="party-generate-qr-btn"
            >
              <QrCode size={18} weight="fill" className="mr-2" />
              {showPartyQr ? "HIDE QR" : `GENERATE QR — ₹${inr2(Number(partyAmt || payParty.total_due))}`}
            </Button>

            {showPartyQr && (
              <QrPanel
                modes={modes}
                currentModeKey={payMode}
                amount={Number(partyAmt) > 0 ? Number(partyAmt) : Number(payParty.total_due)}
                orderNumber={`Party ${payParty.mobile || payParty.customer_name}`}
              />
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline"
                onClick={() => { setPayParty(null); setPartyAmt(""); setPartyDiscountAmt(""); setPartyNote(""); }}
                className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button type="submit" disabled={busy || (Number(partyAmt) <= 0 && Number(partyDiscountAmt) <= 0)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest rounded-sm h-11 px-6"
                data-testid="party-payment-submit">
                <CurrencyInr size={16} weight="bold" className="mr-1" />
                {busy ? "SAVING…" : (() => {
                  const n = Number(partyAmt) || 0;
                  const d = Number(partyDiscountAmt) || 0;
                  if (n > 0 && d > 0) return `PAY ₹${inr2(n)} + WAIVE ₹${inr2(d)}`;
                  if (d > 0 && n <= 0) return `WAIVE ₹${inr2(d)}`;
                  return `PAY IN ₹${inr2(n)}`;
                })()}
              </Button>
            </div>
          </form>
        ) : !selected ? (
          /* Step 1 — search order */
          <div className="space-y-3 min-w-0">
            <div>
              <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">
                FIND ORDER
              </Label>
              <div className="relative mt-1.5">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Order # or Customer Mobile…"
                  autoFocus
                  className="pl-9 bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num"
                  data-testid="quick-payment-search-input"
                />
              </div>
              <div className="text-xs text-slate-500 mt-1.5">
                Type at least 2 characters. Party ke saare orders ek jagah dikhenge.
              </div>
            </div>

            {/* NEW CUSTOMER ADVANCE — for brand-new party with no orders yet */}
            <Button
              type="button"
              onClick={() => openAdvance({ isNew: true, mobile: "", customer_name: "", address: "" })}
              variant="outline"
              className="w-full h-10 rounded-sm border-cyan-500/50 bg-cyan-500/5 hover:bg-cyan-500/15 text-cyan-300 font-heading tracking-widest text-xs"
              data-testid="new-customer-advance-btn"
            >
              + NEW CUSTOMER ADVANCE (bina order ke)
            </Button>

            <div className="max-h-72 overflow-y-auto space-y-1.5">
              {parties.length === 0 && q && (
                <div className="text-slate-500 text-sm py-6 text-center">
                  No orders match.
                </div>
              )}

              {/* Party drill-down: user tapped a customer card → show that
                  party's individual orders so they pick which one to pay. */}
              {partyDrill ? (
                <>
                  <button
                    type="button"
                    onClick={() => setPartyDrill(null)}
                    className="flex items-center gap-1 text-xs font-mono-num text-slate-400 hover:text-orange-300 mb-2"
                    data-testid="party-drill-back"
                  >
                    <ArrowLeft size={12} /> BACK TO ALL PARTIES
                  </button>
                  <div className="mb-3 p-3 rounded-sm bg-orange-500/10 border border-orange-500/40">
                    <div className="text-[10px] font-mono-num tracking-widest text-orange-400">TOTAL DUE</div>
                    <div className="font-heading text-2xl font-black text-orange-300 leading-none mt-0.5">
                      ₹{inr2(partyDrill.total_due)}
                    </div>
                    <div className="text-xs text-slate-300 mt-1">
                      {partyDrill.customer_name}{partyDrill.mobile ? ` · ${partyDrill.mobile}` : ""}
                    </div>
                  </div>
                  {partyDrill.orders.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => pickOrder(r.id, partyDrill)}
                      data-testid={`quick-payment-result-${r.order_number}`}
                      className="w-full flex items-center justify-between p-3 rounded-sm bg-slate-800/40 hover:bg-slate-800 border border-slate-700 hover:border-orange-500/40 transition-colors text-left"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono-num text-orange-400">#{r.order_number}</span>
                          <StatusBadge status={r.status} />
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">PENDING</div>
                        <div className={
                          "font-mono-num font-semibold " +
                          (pendingOf(partyDrill, r) > 0 ? "text-orange-400" : "text-emerald-400")
                        } data-testid={`quick-payment-result-pending-${r.order_number}`}>
                          ₹{inr2(pendingOf(partyDrill, r))}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              ) : (
                /* Grouped list — one card per party with total due */
                parties.map((p) => {
                  const openOrders = p.pending_count;
                  return (
                    <div
                      key={p.key}
                      className="w-full p-3 rounded-sm bg-slate-800/40 border border-slate-700 hover:border-slate-600 transition-colors"
                      data-testid={`quick-payment-party-${p.key}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            if (p.orders.length === 1) pickOrder(p.orders[0].id, p);
                            else setPartyDrill(p);
                          }}
                          className="text-left min-w-0 flex-1 group"
                        >
                          <div className="text-sm text-slate-100 font-heading font-black truncate group-hover:text-orange-300 transition-colors">
                            {p.customer_name}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 font-mono-num truncate">
                            <span data-testid={`party-pending-count-${p.key}`}>{p.mobile ? formatPhone(p.mobile) : "—"} · {openOrders} pending</span> / {p.authoritative_order_count ?? p.orders.length} order{(p.authoritative_order_count ?? p.orders.length) === 1 ? "" : "s"} · tap for details
                          </div>
                        </button>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">TOTAL DUE</div>
                          <div className={
                            "font-mono-num font-black text-lg whitespace-nowrap " +
                            (p.total_due > 0 ? "text-orange-400" : "text-emerald-400")
                          } data-testid={`party-total-due-${p.key}`}>
                            ₹{inr2(Math.max(p.total_due, 0))}
                          </div>
                          {p.advance_credit > 0 && (
                            <div className="text-[10px] font-mono-num text-cyan-300" data-testid={`party-advance-credit-${p.key}`}>ADVANCE ₹{inr2(p.advance_credit)}</div>
                          )}
                        </div>
                      </div>
                      {p.total_due > 0 ? (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPayParty(p);
                              setPartyAmt(String(p.total_due.toFixed(2)));
                            }}
                            className="h-10 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-heading tracking-widest text-xs"
                            data-testid={`pay-due-btn-${p.key}`}
                          >
                            <CurrencyInr size={16} weight="bold" className="mr-1" />
                            TAKE PAYMENT (₹{inr2(p.total_due)} due)
                          </Button>
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAdvance({
                                isNew: false,
                                mobile: p.mobile,
                                customer_name: p.customer_name,
                                address: p.orders?.[0]?.address || "",
                              });
                            }}
                            variant="outline"
                            className="h-10 rounded-sm border-cyan-500/50 bg-cyan-500/5 hover:bg-cyan-500/15 text-cyan-300 font-heading tracking-widest text-xs"
                            data-testid={`take-advance-btn-${p.key}`}
                          >
                            + ADVANCE
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAdvance({
                              isNew: false,
                              mobile: p.mobile,
                              customer_name: p.customer_name,
                              address: p.orders?.[0]?.address || "",
                            });
                          }}
                          variant="outline"
                          className="mt-3 w-full h-10 rounded-sm border-cyan-500/50 bg-cyan-500/5 hover:bg-cyan-500/15 text-cyan-300 font-heading tracking-widest text-xs"
                          data-testid={`take-advance-btn-${p.key}`}
                        >
                          <CurrencyInr size={16} weight="bold" className="mr-1" />
                          TAKE ADVANCE {p.advance_credit > 0 ? `(₹${inr2(p.advance_credit)} advance jama)` : "(₹0 due)"}
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          /* Step 2 — payment form */
          <form onSubmit={submit} className="space-y-4" data-testid="quick-payment-form">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-white p-1"
                data-testid="quick-payment-back"
              >
                <ArrowLeft size={16} />
              </button>
              <div className="flex items-center gap-2 min-w-0">
                <Package size={18} className="text-orange-400 shrink-0" />
                <span className="font-mono-num text-orange-400">#{selected.order_number}</span>
                <span className="text-slate-500">·</span>
                <span className="text-slate-200 truncate">{selected.customer_name}</span>
              </div>
            </div>

            {/* Balance banner */}
            <div className={
              "rounded-sm border p-4 grid grid-cols-3 gap-2 " +
              (balance > 0 ? "border-orange-500/40 bg-orange-500/5" : "border-emerald-500/40 bg-emerald-500/5")
            }>
              <div>
                <div className="text-[10px] font-mono-num tracking-widest text-slate-500">TOTAL</div>
                <div className="font-mono-num text-sm text-slate-200">
                  ₹{inr2(selected.total_amount)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-num tracking-widest text-slate-500">RECEIVED</div>
                <div className="font-mono-num text-sm text-cyan-300">
                  ₹{inr2(selected.advance_paid)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-mono-num tracking-widest text-slate-500">
                  {balance > 0 ? "PENDING" : "FULLY PAID"}
                </div>
                <div
                  className={
                    "font-mono-num text-2xl font-bold " +
                    (balance > 0 ? "text-orange-400" : "text-emerald-400")
                  }
                  data-testid="quick-payment-pending"
                >
                  ₹{inr2(balance)}
                </div>
              </div>
            </div>

            {selected.fifo_pending != null && Math.abs((selected.total_amount - (selected.discount || 0) - selected.advance_paid) - balance) > 0.009 && (
              <div className="text-xs bg-cyan-500/5 border border-cyan-500/30 rounded-sm px-3 py-2 text-cyan-200 font-mono-num" data-testid="quick-payment-fifo-adjusted">
                {(selected.total_amount - (selected.discount || 0) - selected.advance_paid) > balance
                  ? `Adjusted from older orders' extra payment: ₹${inr2((selected.total_amount - (selected.discount || 0) - selected.advance_paid) - balance)}`
                  : `Extra paid on this order, adjusted to newer orders: ₹${inr2(balance - (selected.total_amount - (selected.discount || 0) - selected.advance_paid))}`}
              </div>
            )}
            {selected.discount > 0 && (
              <div className="flex items-center justify-between text-xs bg-amber-500/5 border border-amber-500/30 rounded-sm px-3 py-2">
                <span className="text-amber-300 font-mono-num tracking-widest">
                  DISCOUNT / WRITE-OFF APPLIED
                </span>
                <span className="font-mono-num text-amber-300 font-semibold">
                  ₹{inr2(selected.discount)}
                </span>
              </div>
            )}

            {balance <= 0 ? (
              <div className="text-center py-6 text-emerald-400 flex items-center justify-center gap-2">
                <CheckCircle size={20} weight="fill" />
                <span>This order is already fully paid.</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT (₹)</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={payAmt} onChange={(e) => setPayAmt(e.target.value)}
                      className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm font-mono-num text-lg mt-1.5"
                      data-testid="quick-payment-amount"
                      autoFocus
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
                    <Select value={payMode} onValueChange={setPayMode}>
                      <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-11 rounded-sm mt-1.5" data-testid="quick-payment-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-600 text-white">
                        {modes.map((m) => (
                          <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Discount inline — same row as amount for parity */}
                <div>
                  <Label className="text-amber-300 font-mono-num text-[10px] tracking-widest flex items-center justify-between">
                    <span>DISCOUNT / WRITE-OFF (₹)</span>
                    <span className="text-slate-500 normal-case tracking-normal">
                      customer nahi de raha? yaha likh do
                    </span>
                  </Label>
                  <div className="flex gap-1 mt-1.5" data-testid="quick-payment-discount-type">
                    {[["flat", "₹ FLAT"], ["pct", "%"], ["per_sqft", "₹/SQFT"]].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => applyDisc(k, discVal)}
                        disabled={k === "per_sqft" && !(selected?.total_sqft > 0)}
                        className={"h-8 px-3 rounded-sm border text-[11px] font-mono-num disabled:opacity-40 " + (discType === k ? "bg-amber-500/25 border-amber-500 text-amber-200" : "border-slate-600 text-slate-400")}
                        data-testid={`quick-payment-discount-type-${k}`}>{l}</button>
                    ))}
                  </div>
                  <Input
                    type="number" min="0" step="0.01"
                    value={discVal} onChange={(e) => applyDisc(discType, e.target.value)}
                    placeholder="0"
                    className="bg-slate-800/60 border-amber-500/40 text-amber-200 placeholder:text-slate-600 h-11 rounded-sm font-mono-num text-lg mt-1.5 focus:border-amber-500"
                    data-testid="quick-payment-discount-amount"
                  />
                  {discType !== "flat" && Number(discountAmt) > 0 && (
                    <div className="text-[11px] font-mono-num text-amber-300 mt-1" data-testid="quick-payment-discount-computed">= ₹{inr2(Number(discountAmt))} discount</div>
                  )}
                </div>

                <Button
                  type="button"
                  onClick={() => setPayAmt(String(balance))}
                  variant="outline"
                  className="w-full rounded-sm border-orange-500/60 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 h-11 font-heading tracking-widest"
                  data-testid="quick-payment-pay-full"
                >
                  <CurrencyInr size={16} className="mr-1" />
                  PAY FULL BALANCE — ₹{inr2(balance)}
                </Button>

                {/* Generate QR — customer scans → pays online exactly the AMOUNT typed above */}
                <Button
                  type="button"
                  onClick={() => setShowQr((v) => !v)}
                  variant="outline"
                  className="w-full rounded-sm border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 h-11 font-heading tracking-widest"
                  data-testid="quick-payment-generate-qr"
                >
                  <QrCode size={16} className="mr-1" />
                  {showQr ? "HIDE QR" : "GENERATE QR"}
                </Button>

                {showQr && (
                  <QrPanel
                    modes={modes}
                    currentModeKey={payMode}
                    amount={Number(payAmt || 0)}
                    orderNumber={selected.order_number}
                  />
                )}

                <div>
                  <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">NOTE (OPTIONAL)</Label>
                  <Input
                    value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g., Final payment on delivery"
                    className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm mt-1.5"
                    data-testid="quick-payment-note"
                  />
                </div>

                {canAutoDeliver && (
                  <label className="flex items-center gap-2 p-3 border border-emerald-500/40 bg-emerald-500/5 rounded-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={markDelivered}
                      onChange={(e) => setMarkDelivered(e.target.checked)}
                      className="accent-emerald-500"
                      data-testid="quick-payment-mark-delivered"
                    />
                    <span className="text-sm text-emerald-300">
                      Also mark <b>#{selected.order_number}</b> as <b>Delivered &amp; Closed</b>
                    </span>
                  </label>
                )}

                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full btn-industrial rounded-sm h-12 text-base"
                  data-testid="quick-payment-submit"
                >
                  {busy ? "SAVING…" : "SAVE"}
                </Button>
              </>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* -----------------------------------------------------------
   Inline QR panel — renders the UPI QR right inside Log Payment
   using the AMOUNT the admin typed. Customer cannot lower this
   amount when they scan (UPI protocol locks the payee & amount).
------------------------------------------------------------ */
function QrPanel({ modes, currentModeKey, amount, orderNumber }) {
  const [modeKey, setModeKey] = useState(currentModeKey);

  // Only modes that have UPI ID can produce a QR
  const upiModes = (modes || []).filter((m) => (m.upi_id || "").trim());
  // Check if the parent's chosen mode specifically has a UPI ID. If NOT,
  // we surface a warning instead of silently falling back to some other
  // bank's QR (which was the original bug the user reported: pick UCO,
  // see IndusInd QR).
  const parentMode = (modes || []).find((m) => m.key === currentModeKey);
  const parentHasUpi = !!(parentMode && (parentMode.upi_id || "").trim());
  const selectedMode = upiModes.find((m) => m.key === modeKey) || upiModes[0];

  // Sync inner state whenever the parent's chosen mode changes — critical
  // so switching the MODE dropdown outside the panel re-renders the QR for
  // that bank account (e.g. UCO selected → UCO QR, not the first upi mode).
  useEffect(() => {
    if (currentModeKey && currentModeKey !== modeKey) {
      setModeKey(currentModeKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModeKey]);

  useEffect(() => {
    if (selectedMode && selectedMode.key !== modeKey) setModeKey(selectedMode.key);
  }, [selectedMode, modeKey]);

  // Warn if the ADMIN-picked mode (in MODE dropdown) has no UPI configured.
  // We still let them pick a different UPI-enabled mode via the internal
  // selector below, but this surface message prevents silent bank mismatch.
  const showMismatchWarning = !parentHasUpi && parentMode && upiModes.length > 0;

  if (upiModes.length === 0) {
    return (
      <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200" data-testid="qr-no-upi-warning">
        <div className="font-heading tracking-widest text-amber-300 mb-1">NO UPI ID CONFIGURED</div>
        <div className="text-slate-300 text-xs leading-relaxed">
          QR banane ke liye <b>Settings → Payment Methods</b> mein jaake kisi bhi Bank Account ki
          <b> UPI ID</b> field bharo (e.g. <span className="font-mono-num">9122149006@paytm</span>).
          Uske baad wapas aake QR generate hoga.
        </div>
      </div>
    );
  }

  if (!amount || amount <= 0) {
    return (
      <div className="rounded-sm border border-slate-700 bg-slate-800/40 p-4 text-sm text-slate-400" data-testid="qr-need-amount">
        Amount daalo upar wale field mein — QR usi amount ke liye lock ho jayega.
      </div>
    );
  }

  const note = `Order #${orderNumber || ""}`;
  const qrSrc = `${BACKEND_BASE}/api/settings/payment-modes/${selectedMode.key}/qr?amount=${encodeURIComponent(amount)}&note=${encodeURIComponent(note)}`;

  const download = () => {
    const a = document.createElement("a");
    a.href = qrSrc;
    a.download = `QR-${orderNumber || selectedMode.key}-${amount}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="rounded-sm border border-emerald-500/40 bg-slate-950 p-3" data-testid="qr-panel">
      {showMismatchWarning && (
        <div className="mb-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200" data-testid="qr-mode-mismatch">
          <div className="font-heading tracking-widest text-amber-300 mb-0.5">
            "{parentMode.label}" me UPI ID nahi hai
          </div>
          <div className="text-slate-300 text-[11px] leading-relaxed">
            QR ke liye niche se koi UPI-enabled bank chuno, ya <b>Settings → Payment Methods</b> me
            "{parentMode.label}" ki UPI ID add karo.
          </div>
        </div>
      )}
      {(upiModes.length > 1 || showMismatchWarning) && (
        <div className="mb-2">
          <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">RECEIVE INTO</Label>
          <Select value={selectedMode.key} onValueChange={setModeKey}>
            <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm mt-1.5" data-testid="qr-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-600 text-white">
              {upiModes.map((m) => (
                <SelectItem key={m.key} value={m.key}>{m.label} · {m.upi_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="bg-white rounded-sm p-3 flex flex-col items-center gap-2">
        <img
          src={qrSrc}
          alt={`UPI QR for ₹${amount}`}
          className="w-56 h-56"
          data-testid="qr-panel-image"
        />
        <div className="text-center">
          <div className="font-heading text-2xl font-black text-slate-900">₹{inr2(amount)}</div>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-600 mt-0.5">
            {selectedMode.upi_id} · {selectedMode.account_holder_name || selectedMode.label}
          </div>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mt-1">
            SCAN & PAY — ANY UPI APP (GPay · PhonePe · Paytm · BHIM)
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <Button
          type="button"
          onClick={download}
          variant="outline"
          className="flex-1 rounded-sm border-slate-600 text-slate-200 hover:bg-slate-800 h-9 font-heading tracking-widest"
          data-testid="qr-download-btn"
        >
          DOWNLOAD & SHARE
        </Button>
      </div>
      <div className="text-[10px] font-mono-num tracking-widest text-emerald-300 mt-2 text-center">
        ✓ Amount LOCKED — customer kam nahi kar sakta
      </div>
    </div>
  );
}
